'use client';

import { useGsapContext } from 'hooks/animation/useGsapContext';
import gsap from 'gsap';
import { type CSSProperties } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { Container, CustomImage as Image } from '../..';
import { H1, P } from '../styleConstants';
import { SearchComponent } from './SearchCpmponent';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useTranslations } from 'next-intl';
import styles from './Hero.module.css';

const Marquee = ({ className }: { className?: string }) => {
  interface Config {
    repeat?: number;
    paused?: boolean;
    speed?: number;
    snap?: boolean | number;
    paddingRight?: number;
    reversed?: boolean;
  }

  const t = useTranslations('forWork');
  const gsapContext = useGsapContext();

  const horizontalLoop = (items: HTMLElement[], config: Config) => {
    gsapContext.add(() => {
      config = config || {};
      const tl = gsap.timeline({
        repeat: config.repeat,
        paused: config.paused,
        defaults: { ease: 'none' },
        onReverseComplete: () => {
          tl.totalTime(tl.rawTime() + tl.duration() * 100);
        },
      });
      const length = items.length;
      const startX = items[0].offsetLeft;
      const times: number[] = [];
      const widths: number[] = [];
      const xPercents: number[] = [];
      let curIndex = 0;
      const pixelsPerSecond = (config.speed || 1) * 100;
      const snap =
        config.snap === false
          ? (v: number) => v
          : gsap.utils.snap(
              typeof config.snap === 'boolean' ? 1 : config.snap || 1,
            );
      let totalWidth: number;
      let curX: number;
      let distanceToStart: number;
      let distanceToLoop: number;

      gsap.set(items, {
        xPercent: (i, el) => {
          const w = (widths[i] = parseFloat(
            gsap.getProperty(el, 'width', 'px') as string,
          ));
          xPercents[i] = snap(
            (parseFloat(gsap.getProperty(el, 'x', 'px') as string) / w) * 100 +
              (gsap.getProperty(el, 'xPercent') as number),
          );
          return xPercents[i];
        },
      });

      gsap.set(items, { x: 0 });

      // eslint-disable-next-line prefer-const
      totalWidth =
        items[length - 1].offsetLeft +
        (xPercents[length - 1] / 100) * widths[length - 1] -
        startX +
        items[length - 1].offsetWidth *
          (gsap.getProperty(items[length - 1], 'scaleX') as number) +
        (parseFloat(config.paddingRight?.toString() || '0') || 0);

      for (let i = 0; i < length; i++) {
        const item = items[i];
        curX = (xPercents[i] / 100) * widths[i];
        distanceToStart = item.offsetLeft + curX - startX;
        distanceToLoop =
          distanceToStart +
          widths[i] * (gsap.getProperty(item, 'scaleX') as number);

        tl.to(
          item,
          {
            xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
            duration: distanceToLoop / pixelsPerSecond,
          },
          0,
        )
          .fromTo(
            item,
            {
              xPercent: snap(
                ((curX - distanceToLoop + totalWidth) / widths[i]) * 100,
              ),
            },
            {
              xPercent: xPercents[i],
              duration:
                (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
              immediateRender: false,
            },
            distanceToLoop / pixelsPerSecond,
          )
          .add('label' + i, distanceToStart / pixelsPerSecond);
        times[i] = distanceToStart / pixelsPerSecond;
      }

      const toIndex = (index: number, vars: gsap.TweenVars = {}) => {
        Math.abs(index - curIndex) > length / 2 &&
          (index += index > curIndex ? -length : length);
        const newIndex = gsap.utils.wrap(0, length, index);
        let time = times[newIndex];
        if (time > tl.time() !== index > curIndex) {
          vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
          time += tl.duration() * (index > curIndex ? 1 : -1);
        }
        curIndex = newIndex;
        vars.overwrite = true;
        return tl.tweenTo(time, vars);
      };

      tl.next = (vars?: gsap.TweenVars) => toIndex(curIndex + 1, vars);
      tl.previous = (vars?: gsap.TweenVars) => toIndex(curIndex - 1, vars);
      tl.current = () => curIndex;
      tl.toIndex = (index: number, vars?: gsap.TweenVars) =>
        toIndex(index, vars);
      tl.times = times;
      tl.progress(1, true).progress(0, true);

      if (config.reversed) {
        tl.vars.onReverseComplete?.();
        tl.reverse();
      }

      return tl;
    });
  };

  useIsomorphicLayoutEffect(() => {
    setTimeout(() => {
      const items = gsap.utils.toArray<HTMLElement>('.marquee-item');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const loopTimeline = horizontalLoop(items, {
        repeat: -1,
        paused: false,
        speed: 1,
      });
    }, 1000);
  }, []);

  return (
    <div className={className ? `${styles.marquee} ${className}` : styles.marquee}>
      <div className={styles.caption}>{t('hero.ecosystemCaption')}</div>
      <div className={styles.marqueeInner}>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/cred.png"
            width="3500"
            height="1400"
            alt="zomato"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/zomato.png"
            width="3500"
            height="1400"
            alt="zomato"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/alphawave.png"
            width="3500"
            height="1400"
            alt="alpha wave"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/angellist.png"
            width="3500"
            height="1400"
            alt="angel list"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/blume.png"
            width="3500"
            height="1400"
            alt="blume"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/nexusvp.png"
            width="3500"
            height="1400"
            alt="nexusvp"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/nvidia.png"
            width="3500"
            height="1400"
            alt="nvidia"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/openai.png"
            width="3500"
            height="1400"
            alt="openai"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/peak15.png"
            width="3500"
            height="1400"
            alt="peak15"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/slack.png"
            width="3500"
            height="1400"
            alt="slack"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/spacex.png"
            width="3500"
            height="1400"
            alt="spacex"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/tesla.png"
            width="3500"
            height="1400"
            alt="tesla"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/twh-2.png"
            width="3500"
            height="1400"
            alt="the whole truth"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/uber-2.png"
            width="3500"
            height="1400"
            alt="uber"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/zerodha.png"
            width="3500"
            height="1400"
            alt="zerodha"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/perplexity.png"
            width="3500"
            height="1400"
            alt="the whole truth"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/softbank.png"
            width="3500"
            height="1400"
            alt="uber"
          />
        </span>
        <span className={`${styles.marqueeItem} marquee-item`}>
          <Image
            src="web_v2/affiliate/work/steadview.png"
            width="3500"
            height="1400"
            alt="zerodha"
          />
        </span>
      </div>
    </div>
  );
};

export const Hero = ({ className }: { className?: string }) => {
  const t = useTranslations('forWork');
  return (
    <section
      className={className ? `${styles.root} ${className}` : styles.root}
      style={
        {
          '--hero-bg': `url(${getCompressedAssetUrl(
            '/web_v2/lifestyle/ultrawork-ring-hands.png',
          )})`,
        } as CSSProperties
      }
    >
      <Container>
        <H1>{t('hero.title')} </H1>
        <P>{t('hero.subtitle')}</P>
        <SearchComponent />
      </Container>
      <Marquee />
    </section>
  );
};
